import type { ComponentPropsWithoutRef } from "react";

import normalIcon14 from "@/assets/figma/label-tag/normal-14.svg";
import normalIcon16 from "@/assets/figma/label-tag/normal-16.svg";
import priorityIcon14 from "@/assets/figma/label-tag/priority-14.svg";
import priorityIcon16 from "@/assets/figma/label-tag/priority-16.svg";
import suggestionIcon14 from "@/assets/figma/label-tag/suggestion-14.svg";
import suggestionIcon16 from "@/assets/figma/label-tag/suggestion-16.svg";
import urgentIcon14 from "@/assets/figma/label-tag/urgent-14.svg";
import urgentIcon16 from "@/assets/figma/label-tag/urgent-16.svg";
import { cn } from "@/shared/lib/cn";

export type MerryLabelTagVariant =
  | "suggestion"
  | "priority"
  | "urgent"
  | "normal";

export type MerryLabelTagSize = 14 | 16;

export interface MerryLabelTagProps
  extends Omit<ComponentPropsWithoutRef<"span">, "children"> {
  label: string;
  size?: MerryLabelTagSize;
  variant: MerryLabelTagVariant;
  withIcon?: boolean;
}

type IconVisibility = "withIcon" | "withoutIcon";

interface LabelTagVariantDefinition {
  icons: Record<MerryLabelTagSize, string>;
  nodeIds: Record<
    MerryLabelTagSize,
    Record<IconVisibility, string>
  >;
}

const variantDefinitions: Record<
  MerryLabelTagVariant,
  LabelTagVariantDefinition
> = {
  suggestion: {
    icons: {
      14: suggestionIcon14,
      16: suggestionIcon16,
    },
    nodeIds: {
      14: { withIcon: "300:31353", withoutIcon: "300:31360" },
      16: { withIcon: "300:31351", withoutIcon: "300:31357" },
    },
  },
  priority: {
    icons: {
      14: priorityIcon14,
      16: priorityIcon16,
    },
    nodeIds: {
      14: { withIcon: "300:31372", withoutIcon: "300:31375" },
      16: { withIcon: "300:31367", withoutIcon: "300:31370" },
    },
  },
  urgent: {
    icons: {
      14: urgentIcon14,
      16: urgentIcon16,
    },
    nodeIds: {
      14: { withIcon: "300:31384", withoutIcon: "300:31387" },
      16: { withIcon: "300:31379", withoutIcon: "300:31382" },
    },
  },
  normal: {
    icons: {
      14: normalIcon14,
      16: normalIcon16,
    },
    nodeIds: {
      14: { withIcon: "300:31396", withoutIcon: "300:31399" },
      16: { withIcon: "300:31391", withoutIcon: "300:31394" },
    },
  },
};

export function MerryLabelTag({
  className,
  label,
  size = 16,
  variant,
  withIcon = false,
  ...props
}: MerryLabelTagProps) {
  const definition = variantDefinitions[variant];
  const iconVisibility = withIcon ? "withIcon" : "withoutIcon";

  return (
    <span
      {...props}
      className={cn("merry-label-tag", className)}
      data-figma-node-id={definition.nodeIds[size][iconVisibility]}
      data-label-tag-icon={String(withIcon)}
      data-label-tag-size={size}
      data-label-tag-variant={variant}
    >
      {withIcon ? (
        <span aria-hidden="true" className="merry-label-tag__icon">
          <img alt="" src={definition.icons[size]} />
        </span>
      ) : null}
      <span className="merry-label-tag__label">{label}</span>
    </span>
  );
}
